import { useMutation } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { ExternalLink, Loader2 } from "lucide-react";
import { PageHeader } from "../../shared/ui/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { messagingApi, type MessagingConsoleLink } from "../../shared/api/messaging";
import { apiError } from "../../shared/api/client";

/**
 * #18746 — the "Tin nhắn" entry point.
 *
 * The link is minted on demand rather than on page load: it is single-use and lives 60 seconds at
 * the platform, so one fetched when the page opened would usually be dead by the time anyone
 * clicked it. Opening in a new tab keeps the clinic's HIS session where it was.
 */
const MESSAGE: Record<MessagingConsoleLink["state"], { title: string; body: string }> = {
  READY: { title: "", body: "" },
  // Cố ý KHÔNG hứa "xong trong ít phút". Câu đó từng đứng ở đây và nó sai theo cách tệ nhất:
  // trạng thái này có hai nguyên nhân cùng hình dạng — sự kiện provisioning chưa chạy xong (vài
  // phút thật), hoặc nó CHƯA TỪNG được phát vì messaging được bật sau khi phòng khám đăng ký
  // (vĩnh viễn cho tới khi có người làm gì đó). Trên production `tenant.provisioned.v1` chưa từng
  // được phát lần nào, nên vế thứ hai mới là vế thường gặp — và người dùng đã ngồi đợi một việc
  // không hề đang chạy.
  NOT_PROVISIONED: {
    title: "Phòng khám chưa có tài khoản nhắn tin",
    body:
      "Nếu phòng khám vừa được đăng ký, tài khoản có thể đang được tạo — thử lại sau ít phút. " +
      "Nếu đã đăng ký từ lâu thì cần quản trị viên Addy kiểm tra, chờ thêm sẽ không giải quyết.",
  },
  NOT_CONFIGURED: {
    title: "Hệ thống chưa bật dịch vụ nhắn tin",
    body:
      "Bản triển khai này chưa được cấu hình để kết nối Addy Message. Đây là việc của quản trị " +
      "viên hệ thống, không phải của phòng khám.",
  },
  FAILED: {
    title: "Chưa kết nối được tới Addy Message",
    body:
      "Hệ thống nhắn tin không phản hồi. Dữ liệu của phòng khám không bị ảnh hưởng — hãy thử " +
      "lại, nếu vẫn lỗi thì báo quản trị viên.",
  },
  SUSPENDED: {
    title: "Dịch vụ nhắn tin đang tạm ngưng",
    body: "Phòng khám đang bị tạm ngưng dịch vụ nhắn tin. Liên hệ Addy để được mở lại.",
  },
};

/**
 * #19667 — pages other screens send the clinic to (`/messaging?next=/app/sms`), so a "connect SMS"
 * link lands on the SMS provider screen instead of the console's front page. Anything not listed
 * here is ignored and the console opens on its dashboard, as before.
 */
export const CONSOLE_PAGES: Record<string, string> = {
  "/app/sms": "Nhà cung cấp SMS",
  "/app/sms-messages": "Tin SMS đã gửi",
  "/app/zbs-messages": "Tin Zalo đã gửi",
  "/app/email-server": "Máy chủ Email (SMTP)",
  "/app/email-messages": "Email đã gửi",
};

export function MessagingConsolePage() {
  const [params] = useSearchParams();
  const requested = params.get("next");
  const next = requested && CONSOLE_PAGES[requested] ? requested : undefined;
  const open = useMutation({
    mutationFn: () => messagingApi.ssoUrl(next),
    onSuccess: (link) => {
      if (link.state === "READY" && link.url) window.open(link.url, "_blank", "noopener");
    },
  });

  const link = open.data;
  const state = link?.state;
  const notice = state && state !== "READY" ? MESSAGE[state] : null;

  return (
    <div className="font-sans">
      <PageHeader
        title="Tin nhắn"
        description="Mở console nhắn tin của phòng khám trên Addy Message — xem tin đã gửi, mẫu tin và báo cáo."
      />

      <Card className="max-w-2xl">
        <CardContent className="grid gap-4 pt-6">
          <p className="text-sm text-muted-foreground">
            Bạn sẽ được đăng nhập thẳng bằng tài khoản HIS hiện tại, không cần nhập mật khẩu lần
            thứ hai. Console mở ở tab mới.
          </p>

          <div>
            <Button onClick={() => open.mutate()} disabled={open.isPending}>
              {open.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Đang mở…
                </>
              ) : (
                <>
                  <ExternalLink className="mr-2 h-4 w-4" /> Mở Addy Message
                  {next && <> → {CONSOLE_PAGES[next]}</>}
                </>
              )}
            </Button>
          </div>

          {notice && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3">
              <p className="text-sm font-medium text-amber-900">{notice.title}</p>
              <p className="mt-1 text-sm text-amber-800">{notice.body}</p>
            </div>
          )}

          {state === "READY" && (
            <p className="text-sm text-emerald-700">
              Đã mở ở tab mới. Nếu trình duyệt chặn cửa sổ bật lên, hãy cho phép rồi bấm lại.
            </p>
          )}

          {open.isError && (
            <p className="text-sm text-destructive">{apiError(open.error).detail}</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
